/**
 * CartRenderers.js
 * Handles all HTML rendering for cart notepad, unpaid orders, and order history
 */

import { CartCalculations } from './CartCalculations.js';

export class CartRenderers {
  /**
   * Create translation helper function
   * @param {Object} i18n - i18n instance
   * @returns {Function} Translation function
   */
  static createTranslator(i18n) {
    return (key, fallback) => {
      try {
        if (i18n && i18n.t) {
          const translation = i18n.t(key);
          return (translation === key) ? fallback : translation;
        }
        return fallback;
      } catch (error) {
        console.warn('[CartNotepad] Translation error:', error);
        return fallback;
      }
    };
  }

  /**
   * Render main notepad HTML
   * @param {Object} context - Rendering context
   * @returns {string} HTML string
   */
  static renderNotepad(context) {
    const { mode, cartItems, pendingOrder, orderState, i18n, newItem } = context;

    if (mode === 'unpaid') {
      return this.renderUnpaidOrders(context);
    }
    if (mode === 'combined') {
      return this.renderCombined(context);
    }

    const t = this.createTranslator(i18n);
    const totalItems = CartCalculations.getTotalItemCount(cartItems);
    const currency = CartCalculations.getCurrency(pendingOrder, cartItems);
    const totalPrice = CartCalculations.getDisplayTotal(orderState, pendingOrder, cartItems);

    let titleText, statusText, iconClass, statusClass = '';

    switch (orderState) {
      case 'pending-payment':
        titleText = t('cart.order_awaiting_payment', 'Order Awaiting Payment');
        statusText = t('cart.complete_payment', 'Complete payment to confirm');
        iconClass = 'fas fa-clock';
        statusClass = 'pending-payment';
        break;
      case 'completed':
        titleText = t('cart.order_completed', 'Order Completed');
        statusText = t('cart.order_confirmed', 'Your order has been confirmed');
        iconClass = 'fas fa-check-circle';
        statusClass = 'completed';
        break;
      default:
        titleText = t('cart.order', 'Order');
        if (totalItems === 1) {
          statusText = `${totalItems} ${t('cart.item', 'item')}`;
        } else {
          statusText = `${totalItems} ${t('cart.items', 'items')}`;
        }
        iconClass = 'fas fa-shopping-bag';
        statusClass = 'cart';
    }

    return `
      <div class="notepad-content">
        <div class="notepad-lines">
          ${cartItems.map(item => `
            <div class="notepad-item ${newItem && newItem.id === item.id ? 'newly-added' : ''}">
              <div class="item-indicator">
                <div class="quantity-badge">${item.quantity}</div>
              </div>
              <div class="item-details">
                <div class="item-name">${item.name}</div>
                ${item.modifiers && item.modifiers.length > 0 ?
                  `<div class="item-modifiers">${item.modifiers.map(mod => mod.name).join(', ')}</div>` :
                  `<div class="item-portion">${t('cart.regular_portion', 'Regular portion')}</div>`
                }
              </div>
              <div class="item-price">${CartCalculations.formatItemPrice(item, currency)}</div>
            </div>
          `).join('')}
          ${cartItems.length === 0 ? `<div class="empty-notepad">${t('cart.no_items', 'No items yet')}</div>` : ''}
        </div>
        <div class="notepad-footer">
          <div class="total-section">
            <div class="total-label">${t('cart.total', 'Total')}</div>
            <div class="total-amount">${totalPrice.toFixed(2)} ${currency}</div>
          </div>
          ${this.renderActionButton(orderState, t)}
        </div>
      </div>
    `;
  }

  /**
   * Render combined cart and unpaid orders view
   * @param {Object} context - Rendering context
   * @returns {string} HTML string
   */
  static renderCombined(context) {
    const { cartItems, unpaidOrders, i18n } = context;
    const t = this.createTranslator(i18n);
    const currency = cartItems[0]?.currency || 'PLN';
    const totalPrice = CartCalculations.calculateCartTotal(cartItems);

    const cartSection = `
      <div class="notepad-content">
        <div class="section cart-section">
          <div class="section-title"><i class="fas fa-shopping-bag"></i> ${t('cart.current_selection','Current Selection')}</div>
          <div class="notepad-lines">
            ${cartItems.length ? cartItems.map(item => `
              <div class="notepad-item">
                <div class="item-indicator"><div class="quantity-badge">${item.quantity}</div></div>
                <div class="item-details">
                  <div class="item-name">${item.name}</div>
                  ${item.modifiers?.length ? `<div class="item-modifiers">${item.modifiers.map(m=>m.name).join(', ')}</div>` : `<div class="item-portion">${t('cart.regular_portion','Regular portion')}</div>`}
                </div>
                <div class="item-price">${(item.totalPrice || item.price*item.quantity).toFixed(2)} ${currency}</div>
              </div>
            `).join('') : `<div class="empty-notepad">${t('cart.no_items','No items yet')}</div>`}
          </div>
          <div class="notepad-footer">
            <div class="total-section">
              <div class="total-label">${t('cart.total','Total')}</div>
              <div class="total-amount">${totalPrice.toFixed(2)} ${currency}</div>
            </div>
            <div class="action-buttons">
              <button class="confirm-cart-btn" ${cartItems.length ? '' : 'disabled'}>
                <span>${t('cart.confirm_order','Confirm')}</span>
                <i class="fas fa-check"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="section unpaid-section">
          ${this.renderUnpaidOrders(context)}
        </div>
      </div>
    `;
    return cartSection;
  }

  /**
   * Render unpaid orders list
   * @param {Object} context - Rendering context
   * @returns {string} HTML string
   */
  static renderUnpaidOrders(context) {
    const { unpaidOrders, i18n } = context;
    const t = this.createTranslator(i18n);

    if (!unpaidOrders || unpaidOrders.length === 0) {
      return `
        <div class="notepad-header">
          <div class="notepad-title">
            <i class="fas fa-receipt"></i>
            <span>${t('cart.unpaid_orders', 'Unpaid Orders')}</span>
          </div>
        </div>
        <div class="notepad-content">
          <div class="empty-cart-message">
            <div class="empty-cart-text">${t('cart.no_unpaid_orders','No unpaid orders yet')}</div>
          </div>
        </div>
      `;
    }

    const sorted = [...unpaidOrders].sort((a,b)=> new Date(a.createdAt)-new Date(b.createdAt));
    const lastId = sorted[sorted.length-1]?.id;

    const sections = sorted.map(order => `
      <div class="unpaid-order" data-order-id="${order.id}">
        <div class="unpaid-header">
          <div class="left">
            <span class="order-number">#${order.orderNumber || order.id.slice(0,6)}</span>
            <span class="order-date">${new Date(order.createdAt).toLocaleTimeString()}</span>
          </div>
          <div class="right">
            <div class="order-total">${order.currency || 'PLN'} ${Number(order.total).toFixed(2)}</div>
            <button class="toggle-btn" data-toggle="${order.id}">
              <i class="fas ${order.id===lastId ? 'fa-chevron-up' : 'fa-chevron-down'}"></i>
            </button>
          </div>
        </div>
        <div class="unpaid-body" style="display:${order.id===lastId?'block':'none'}">
          <div class="order-items-list" style="padding:8px 0;">
            ${(order.items || []).map(it => `
              <div class="unpaid-item" style="display:flex; align-items:flex-start; justify-content:space-between; padding:8px 0; border-bottom:1px solid var(--color-border-light,#eee);">
                <div>
                  <div class="name" style="font-weight:600; color:var(--color-text-primary,#333);">${it.quantity}× ${it.name}</div>
                  ${it.modifiers && it.modifiers.length ? `<div class="mods" style="margin-top:4px; color:var(--color-text-secondary,#666); font-size:12px;">${it.modifiers.map(m => `${m.name}${m.price>0?` (+${m.price.toFixed(2)} ${order.currency||'PLN'})`:''}`).join(', ')}</div>` : ''}
                </div>
                <div class="price" style="font-weight:700; color:var(--color-primary,#2563eb);">${(it.unitPrice * it.quantity).toFixed(2)} ${order.currency || 'PLN'}</div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `).join('');

    return `
      <div class="notepad-header">
        <div class="notepad-title">
          <i class="fas fa-receipt"></i>
          <span>${t('cart.unpaid_orders', 'Unpaid Orders')}</span>
        </div>
      </div>
      <div class="notepad-content">
        <div class="notepad-lines unpaid-list">
          ${sections}
        </div>
        <div class="notepad-footer">
          <button class="pay-all-btn">
            <span>${t('cart.pay_all','Pay All')}</span>
            <i class="fas fa-credit-card"></i>
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Render action button based on order state
   * @param {string} orderState - Current order state
   * @param {Function} t - Translation function
   * @returns {string} HTML string
   */
  static renderActionButton(orderState, t) {
    switch (orderState) {
      case 'pending-payment':
        return `
          <div class="action-buttons">
            <button class="confirm-pending-btn">
              <span>${t('cart.confirm_order', 'Confirm')}</span>
              <i class="fas fa-check"></i>
            </button>
          </div>
        `;
      case 'completed':
        return `
          <button class="order-completed-btn" disabled>
            <span>${t('cart.order_confirmed', 'Order Confirmed')}</span>
            <i class="fas fa-check"></i>
          </button>
        `;
      default:
        return `
          <button class="view-full-order-btn">
            <span>${t('cart.view_full_order', 'Confirm')}</span>
            <i class="fas fa-check"></i>
          </button>
        `;
    }
  }

  /**
   * Render order history HTML when cart is empty
   * @param {Object} historyContent - History content data
   * @returns {string} HTML string
   */
  static renderOrderHistory(historyContent = {}) {
    const title = historyContent.title || 'Order History';
    const subtitle = historyContent.subtitle || 'Your recent orders will appear here';
    const action = historyContent.action || 'Start ordering to see your history';
    const items = historyContent.items || [];

    return `
      <div class="notepad-header">
        <div class="notepad-title">
          <i class="fas fa-history"></i>
          <span>${title}</span>
        </div>
      </div>
      <div class="notepad-content">
        <div class="notepad-lines order-history-content">
          <div class="empty-cart-message">
            <div class="empty-cart-icon">
              <i class="fas fa-shopping-cart" style="opacity: 0.3; font-size: 2.5rem; color: var(--color-text-muted, #666);"></i>
            </div>
            <div class="empty-cart-text">
              <h4 style="margin: 16px 0 8px; color: var(--color-text-primary, #333); font-size: 1.1rem;">${title}</h4>
              <p style="margin: 0 0 16px; color: var(--color-text-muted, #666); font-size: 0.9rem; line-height: 1.4;">${subtitle}</p>
              <div class="empty-cart-action" style="color: var(--color-primary, #c69c7a); font-size: 0.85rem; font-weight: 500;">
                ${action}
              </div>
            </div>
          </div>
          ${items.length > 0 ? items.map(order => `
            <div class="notepad-item history-item">
              <div class="item-details">
                <span class="order-date">${order.date}</span>
                <span class="order-summary">${order.summary}</span>
              </div>
              <div class="order-total">${order.currency || 'PLN'} ${order.total}</div>
            </div>
          `).join('') : ''}
        </div>
        <div class="notepad-footer">
          <button class="view-full-order-btn" style="background: var(--color-primary, #c69c7a); color: white;">
            <span>Browse Menu</span>
            <i class="fas fa-utensils"></i>
          </button>
        </div>
      </div>
    `;
  }
}
